import { PlusCircleOutlined, ThunderboltFilled, ThunderboltOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ICellEditorParams } from 'ag-grid-community'
import { Button, Checkbox, Tooltip, TreeSelect, type TreeSelectProps } from 'antd'
import type React from 'react'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'

const { TreeNode } = TreeSelect

export type HierarchyData = {
  id: string
  name: string
  children?: HierarchyData[]
  level?: string // What level of data this represents, will be used to render a tooltip
}

export type HierarchySelectorProps = ICellEditorParams & {
  treeData: HierarchyData[]
  defaultList?: string[]
  width?: number
  propOverrides?: TreeSelectProps
}

const countLeafNodes = (node) => {
  if (!node.children || node.children.length === 0) {
    return 1 // If no children, it's a leaf node
  }
  return node.children.reduce((total, child) => total + countLeafNodes(child), 0)
}

const countSelectedLeafNodes = (node, selectedValues) => {
  if (!node.children || node.children.length === 0) {
    return selectedValues.includes(node.id) ? 1 : 0 // If it's a leaf node and selected, count it
  }
  return node.children.reduce((total, child) => total + countSelectedLeafNodes(child, selectedValues), 0)
}

const renderTreeNodes = (data, selectedValues, parentHasThunderbolt = false) =>
  data.map((item) => {
    const selectedLeafCount = countSelectedLeafNodes(item, selectedValues)
    const totalLeafCount = countLeafNodes(item)
    const isAllSelected = selectedLeafCount === totalLeafCount

    return (
      <TreeNode
        title={
          <Horizontal justifyContent='space-between'>
            <Texto>{item.name.toUpperCase()}</Texto>
            {!parentHasThunderbolt &&
              totalLeafCount > 1 &&
              (isAllSelected ? (
                <Tooltip trigger='hover' title={`Linked to ${item.level}`}>
                  <div>
                    <Texto appearance='secondary' className='mr-2'>
                      <ThunderboltFilled />
                    </Texto>
                  </div>
                </Tooltip>
              ) : (
                <Texto appearance='secondary' className='mr-2'>
                  {`${selectedLeafCount}/${totalLeafCount}`}
                </Texto>
              ))}
          </Horizontal>
        }
        key={item.id}
        value={item.id}
        searchTerm={item.name}
      >
        {item.children ? renderTreeNodes(item.children, selectedValues, isAllSelected) : null}
      </TreeNode>
    )
  })

const transformTreeData = (treeData, selectedValues) => {
  return renderTreeNodes(treeData, selectedValues)
}

const extractChildValues = (nodes) => {
  return nodes.reduce((acc, node) => {
    if (!node.children || node.children.length === 0) {
      acc.push(node.id) // Add leaf node id
    } else {
      acc = acc.concat(extractChildValues(node.children)) // Recurse into children
    }
    return acc
  }, [])
}

const customDropdown = (menu, selectedValues, handleSelectAll, allSelected: boolean) => {
  return (
    <Vertical style={{ gap: '.5rem' }}>
      <Horizontal style={{ gap: '.5rem' }} className='ml-2'>
        <Checkbox onClick={handleSelectAll} checked={allSelected} />
        <Texto weight='bold'>Select All</Texto>
        {selectedValues?.length > 0 && <Texto appearance='secondary'>{selectedValues.length} Selected</Texto>}
      </Horizontal>
      {menu}
    </Vertical>
  )
}

export const HierarchySelectEditor: React.FC<HierarchySelectorProps> = forwardRef((props, ref) => {
  // The documented ways of creating a custom cell editor dont seem to work
  // This solution was relatively recent and seems to do this trick
  // https://stackoverflow.com/questions/77682891/custom-cell-editor-for-ag-grid
  const [gridValue, setGridValue] = useState(props.value)
  const refInput = useRef(null)
  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return gridValue
      },
      isPopup() {
        return true
      },
      isCancelBeforeStart() {
        return false
      },
      isCancelAfterEnd() {
        return false
      },
    }
  })

  const treeData = useMemo(() => transformTreeData(props.treeData, gridValue), [props.treeData, gridValue])

  const handleChange = (selection) => {
    setGridValue(selection)
  }

  const allValues = useMemo(() => extractChildValues(props.treeData), [props.treeData])
  const allSelected = gridValue.length === allValues.length

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setGridValue([])
      return
    }
    setGridValue(allValues)
  }, [props.treeData, allSelected])

  return (
    <TreeSelect
      ref={refInput}
      showCheckedStrategy={TreeSelect.SHOW_CHILD}
      allowClear
      showSearch
      style={{ maxWidth: props.width || '200px', minWidth: props.width || '200px' }}
      placeholder='Make a selection'
      onChange={handleChange}
      value={gridValue}
      className='mr-2'
      maxTagCount={3}
      multiple
      dropdownRender={(menu) => customDropdown(menu, gridValue, handleSelectAll, allSelected)}
      treeDefaultExpandAll
      treeNodeFilterProp='searchTerm'
      treeCheckable
      showArrow={false}
      defaultOpen
      {...props.propOverrides}
    >
      {treeData}
    </TreeSelect>
  )
})
